import { useAuth } from '../hooks/useAuth';

/**
 * Transient banner shown while the API cannot be reached.
 *
 * The auth context keeps the session and keeps retrying in this state, so the
 * banner is not dismissible — it clears itself the moment `user.getMe`
 * succeeds again. A genuine 401 never lands here; that still routes to login.
 */
export const ApiOfflineBanner = () => {
  const { isApiUnreachable, refetchUser } = useAuth();

  if (!isApiUnreachable) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border-b border-yellow-200" role="status" aria-live="polite">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-1">
            <svg
              className="h-5 w-5 text-yellow-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 5.636a9 9 0 010 12.728m-12.728 0a9 9 0 010-12.728m9.9 9.9a5 5 0 010-7.072m-7.072 0a5 5 0 010 7.072M12 12h.01"
              />
            </svg>
            <p className="text-sm text-yellow-800 flex-1">
              <span className="font-medium">We can&rsquo;t reach the server right now.</span> You
              are still signed in, and we are retrying automatically. Anything you save may not go
              through until the connection is back.
            </p>
          </div>
          <button
            onClick={() => refetchUser()}
            className="text-sm font-medium text-yellow-900 underline hover:text-yellow-950 cursor-pointer bg-transparent border-none p-0"
          >
            Retry now
          </button>
        </div>
      </div>
    </div>
  );
};
